import { formatCurrency } from "../lib/calculations";
import { AlertTriangle } from "lucide-react";

interface ProjectHealthPanelProps {
  projectName: string;
  contractValue: number; // Original Budget
  approvedVariations: number;
  pendingVariations: number;
  totalClaimed: number;
  onClick?: () => void;
}

export function ProjectHealthPanel({
  projectName,
  contractValue,
  approvedVariations,
  pendingVariations,
  totalClaimed,
  onClick,
}: ProjectHealthPanelProps) {
  // 1. Calculations for Progress Bar (Line 1)
  const revisedContract = contractValue + approvedVariations;
  // Use Original Contract Value for percentage as requested
  const progressPercent =
    contractValue > 0 ? Math.min((totalClaimed / contractValue) * 100, 100) : 0;
  // Over limit is still based on Revised (Actual) Budget
  const isOverLimit = totalClaimed > revisedContract;

  // If over limit, how much?
  const overLimitAmount = isOverLimit ? totalClaimed - revisedContract : 0;
  // Visual overflow cap at 20% width
  const overLimitWidth = isOverLimit
    ? Math.min((overLimitAmount / revisedContract) * 100, 20)
    : 0;

  return (
    <div
      className="card hover:shadow-lg transition-all duration-200 border border-gray-100"
      onClick={onClick}
      style={{
        padding: "20px",
        cursor: onClick ? "pointer" : "default",
        background: "white",
        borderRadius: "16px",
      }}
    >
      {/* Header Row */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "16px",
        }}
      >
        <div>
          <h3
            style={{
              fontSize: "1rem",
              fontWeight: 700,
              color: "#1f2937",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              margin: 0,
            }}
          >
            {projectName}
            {isOverLimit && (
              <span
                style={{
                  backgroundColor: "#fef2f2",
                  color: "#dc2626",
                  fontSize: "0.65rem",
                  padding: "2px 8px",
                  borderRadius: "999px",
                  border: "1px solid #fee2e2",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                <AlertTriangle size={10} /> Over Limit
              </span>
            )}
          </h3>
          <div
            style={{
              fontSize: "0.75rem",
              color: "#9ca3af",
              marginTop: "4px",
              display: "flex",
              gap: "12px",
            }}
          >
            <span data-seq-private="">Orig: {formatCurrency(contractValue)}</span>
            <span data-seq-private="" style={{ color: "#2563eb" }}>
              Rev: {formatCurrency(revisedContract)}
            </span>
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div
            style={{
              fontSize: "1.25rem",
              fontWeight: 700,
              color: "#111827",
              lineHeight: 1,
            }}
          >
            {progressPercent.toFixed(2)}%
          </div>
          <div
            style={{
              fontSize: "0.65rem",
              color: "#9ca3af",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginTop: "4px",
            }}
          >
            Complete
          </div>
        </div>
      </div>

      {/* Progress Bar Container */}
      <div
        style={{
          marginBottom: "24px",
          position: "relative",
          paddingTop: "4px",
        }}
      >
        <div
          style={{
            overflow: "hidden",
            height: "12px",
            marginBottom: "4px",
            display: "flex",
            borderRadius: "999px",
            backgroundColor: "#f3f4f6",
          }}
        >
          {/* Claimed */}
          <div
            style={{
              width: `${progressPercent}%`,
              backgroundColor: "#3b82f6",
              transition: "width 0.5s ease",
            }}
          ></div>
          {/* Over Limit Segment */}
          {isOverLimit && (
            <div
              style={{
                width: `${overLimitWidth}%`,
                backgroundColor: "#ef4444",
                backgroundImage:
                  "linear-gradient(45deg,rgba(255,255,255,.15) 25%,transparent 25%,transparent 50%,rgba(255,255,255,.15) 50%,rgba(255,255,255,.15) 75%,transparent 75%,transparent)",
                backgroundSize: "1rem 1rem",
              }}
            ></div>
          )}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: "0.75rem",
            color: "#6b7280",
          }}
        >
          <span data-seq-private="">Claimed: {formatCurrency(totalClaimed)}</span>
          <span>
            Remaining:{" "}
            {formatCurrency(Math.max(0, contractValue - totalClaimed))}
          </span>
        </div>
      </div>

      {/* Variations Footer */}
      <div
        style={{
          backgroundColor: "#f9fafb",
          borderRadius: "12px",
          padding: "12px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: "0.75rem",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div
            style={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor: approvedVariations >= 0 ? "#10b981" : "#ef4444",
            }}
          ></div>
          <div>
            <div style={{ color: "#6b7280" }}>Approved</div>
            <div data-seq-private=""
              style={{
                fontWeight: 600,
                color: approvedVariations >= 0 ? "#047857" : "#b91c1c",
              }}
            >
              {formatCurrency(approvedVariations)}
            </div>
          </div>
        </div>
        <div
          style={{ height: "24px", width: "1px", backgroundColor: "#e5e7eb" }}
        ></div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            textAlign: "right",
          }}
        >
          <div>
            <div style={{ color: "#6b7280" }}>Pending</div>
            <div data-seq-private="" style={{ fontWeight: 600, color: "#d97706" }}>
              {formatCurrency(pendingVariations)}
            </div>
          </div>
          <div
            style={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor: "#f59e0b",
            }}
          ></div>
        </div>
      </div>
    </div>
  );
}
